import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Current step in the creator workflow
 */
import {
  isKnownStep,
  normalizeStepForMode,
  type CreatorEntryMode,
  type CreatorStep,
} from './creatorSteps';

export type { CreatorStep, CreatorEntryMode } from './creatorSteps';

/**
 * How the seller entered the Creator.
 *
 * - 'classic': the canvas editor opens on step 1, as it always has.
 * - 'guided':  step 1 is the motif-first surface; products come second.
 *
 * Deliberately NOT derived from the URL on every read: getInitialStep() below
 * reads window.location once at module init, and Header.handleAddProducts
 * rebuilds the query string from scratch — a URL-backed mode would silently
 * fall back to classic after a per-product placement edit.
 */

export type ProductsStatusFilter = 'all' | 'active' | 'inactive';
/** Step-2 sort order. No popularity: the schema has no such metric (plan KTD9). */
export type ProductsSort = 'default' | 'price-asc' | 'price-desc' | 'name';

/**
 * Current view for multi-position products
 */
export type CanvasView = 'front' | 'back';

/**
 * Editor mode for the main canvas area (Stufe 5.3).
 *
 * - 'design': Normal Fabric.js canvas editing (default)
 * - product-adjust: Per-product placement adjustment mode.
 *   Shows a large product mockup where the user can drag/resize the design.
 *   Changes only affect the overrides for THIS product (Level 2).
 */
export type EditorMode =
  | { type: 'design' }
  | { type: 'product-adjust'; productId: string };

/**
 * Single-product edit mode.
 *
 * When active, the editor is scoped to a single product:
 * - Canvas shows THIS product's PrintArea
 * - Save operations only affect THIS product's DesignPosition
 * - No ProductGrid, no adaptation for other products
 * - Only the 'design' step is accessible (strict navigation)
 *
 * Activated via URL param: ?editProduct=<productId>
 */
export interface SingleProductMode {
  /** The product ID being edited in isolation */
  productId: string;
}

function getInitialStep(): CreatorStep {
  if (typeof window === 'undefined') {
    return 'design';
  }
  const params = new URLSearchParams(window.location.search);
  const stepParam = params.get('step')?.toLowerCase();
  if (stepParam && isKnownStep(stepParam)) {
    return stepParam;
  }
  return 'design';
}

/**
 * Active tool in the canvas editor
 * - 'select': default, click to select/move objects
 * - 'pan': hand tool, drag to pan the canvas viewport
 */
export type ToolMode = 'select' | 'pan';

/**
 * UI Store Interface
 */
interface UIStore {
  // Current Step
  currentStep: CreatorStep;
  setCurrentStep: (step: CreatorStep) => void;

  // How the Creator was entered (see CreatorEntryMode)
  creatorEntryMode: CreatorEntryMode;
  setCreatorEntryMode: (mode: CreatorEntryMode) => void;

  // Zoom
  zoomLevel: number;
  setZoomLevel: (level: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;

  // View (Front/Back)
  currentView: CanvasView;
  setCurrentView: (view: CanvasView) => void;

  // Modals
  isAspectRatioModalOpen: boolean;
  setAspectRatioModalOpen: (open: boolean) => void;

  isUploadModalOpen: boolean;
  setUploadModalOpen: (open: boolean) => void;

  // Sidebar
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Editor Mode (Stufe 5.3)
  editorMode: EditorMode;
  setEditorMode: (mode: EditorMode) => void;

  // Single-Product Edit Mode
  singleProductMode: SingleProductMode | null;
  stepBeforeSingleProductMode: CreatorStep | null;
  setSingleProductMode: (mode: SingleProductMode | null) => void;

  // Loading States
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Products step filters (persisted across navigation)
  productsStatusFilter: ProductsStatusFilter;
  setProductsStatusFilter: (filter: ProductsStatusFilter) => void;
  productsCategory: string;
  setProductsCategory: (slug: string) => void;
  productsManufacturerIds: string[];
  toggleProductsManufacturer: (id: string) => void;
  productsOnlyMatching: boolean;
  setProductsOnlyMatching: (on: boolean) => void;
  productsSort: ProductsSort;
  setProductsSort: (sort: ProductsSort) => void;
  /** Back to the full catalogue; the sort is kept, it is not a filter. */
  resetProductFilters: () => void;
  /** Catalogue step: render the design on packshots instead of showing model shots. */
  catalogShowDesign: boolean;
  setCatalogShowDesign: (on: boolean) => void;

  // Toast/Notifications (simplified)
  toastMessage: string | null;
  showToast: (message: string) => void;
  clearToast: () => void;

  // Active canvas tool
  toolMode: ToolMode;
  setToolMode: (mode: ToolMode) => void;

  // Fullscreen editor mode
  isFullscreenEditor: boolean;
  setFullscreenEditor: (value: boolean) => void;
}

/**
 * UI Store - manages UI state like modals, zoom, steps
 */
export const useUIStore = create<UIStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentStep: getInitialStep(),
      creatorEntryMode: 'classic',
      zoomLevel: 100,
      currentView: 'front',
      editorMode: { type: 'design' } as EditorMode,
      singleProductMode: null,
      stepBeforeSingleProductMode: null,
      isAspectRatioModalOpen: false,
      isUploadModalOpen: false,
      isSidebarCollapsed: false,
      isLoading: false,
      toastMessage: null,
      productsStatusFilter: 'all' as ProductsStatusFilter,
      productsCategory: 'all',
      productsManufacturerIds: [] as string[],
      productsOnlyMatching: false,
      productsSort: 'default' as ProductsSort,
      catalogShowDesign: false,
      toolMode: 'select' as ToolMode,
      isFullscreenEditor: false,

      // Step Navigation (blocked in single-product mode - only 'design' allowed)
      setCurrentStep: (step) => {
        const { singleProductMode } = get();
        if (singleProductMode && step !== 'design') return;
        if (get().currentStep === step) return;
        // A hard swap on purpose: a View Transition cross-faded the whole
        // old page with the whole new one — on a catalogue of 125 cards that
        // read as a double exposure, not as a transition.
        set({ currentStep: step });
      },

      setCreatorEntryMode: (mode) =>
        set((state) => ({
          creatorEntryMode: mode,
          // A step the new mode does not have (the catalogue, in classic)
          // maps to its nearest equivalent instead of rendering nothing.
          currentStep: normalizeStepForMode(state.currentStep, mode),
        })),
      setCatalogShowDesign: (on) => set({ catalogShowDesign: on }),

      // Zoom Controls
      setZoomLevel: (level) =>
        set({ zoomLevel: Math.min(Math.max(level, 25), 200) }),
      zoomIn: () =>
        set((state) => ({
          zoomLevel: Math.min(state.zoomLevel + 10, 200),
        })),
      zoomOut: () =>
        set((state) => ({
          zoomLevel: Math.max(state.zoomLevel - 10, 25),
        })),
      resetZoom: () => set({ zoomLevel: 100 }),

      // View Controls
      setCurrentView: (view) => set({ currentView: view }),

      // Editor Mode (Stufe 5.3)
      setEditorMode: (mode) => set({ editorMode: mode }),

      // Single-Product Edit Mode
      setSingleProductMode: (mode) => {
        if (mode) {
          // Save current step so we can return to it when exiting this mode
          const { currentStep } = get();
          set({
            singleProductMode: mode,
            currentStep: 'design',
            stepBeforeSingleProductMode: currentStep,
          });
        } else {
          // Restore the step the user was on before entering single-product mode
          const { stepBeforeSingleProductMode } = get();
          set({
            singleProductMode: null,
            currentStep: stepBeforeSingleProductMode ?? 'design',
            stepBeforeSingleProductMode: null,
          });
        }
      },

      // Modal Controls
      setAspectRatioModalOpen: (open) => set({ isAspectRatioModalOpen: open }),
      setUploadModalOpen: (open) => set({ isUploadModalOpen: open }),

      // Sidebar Controls
      setSidebarCollapsed: (collapsed) =>
        set({ isSidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

      // Loading Controls
      setIsLoading: (loading) => set({ isLoading: loading }),

      // Products step filters
      setProductsStatusFilter: (filter) =>
        set({ productsStatusFilter: filter }),
      setProductsCategory: (slug) => set({ productsCategory: slug }),
      toggleProductsManufacturer: (id) =>
        set((state) => ({
          productsManufacturerIds: state.productsManufacturerIds.includes(id)
            ? state.productsManufacturerIds.filter((m) => m !== id)
            : [...state.productsManufacturerIds, id],
        })),
      setProductsOnlyMatching: (on) => set({ productsOnlyMatching: on }),
      setProductsSort: (sort) => set({ productsSort: sort }),
      resetProductFilters: () =>
        set({
          productsStatusFilter: 'all',
          productsCategory: 'all',
          productsManufacturerIds: [],
          productsOnlyMatching: false,
        }),

      // Toast Controls
      showToast: (message) => set({ toastMessage: message }),
      clearToast: () => set({ toastMessage: null }),

      // Tool Mode
      setToolMode: (mode) => set({ toolMode: mode }),

      // Fullscreen Editor
      setFullscreenEditor: (value) => set({ isFullscreenEditor: value }),
    }),
    { name: 'ui-store' }
  )
);
